"use client";

import Link from "next/link";
import { useEffect, useState, useSyncExternalStore } from "react";
import { Button } from "@/components/ui/Button";
import { Container } from "@/components/ui/Container";
import { langPath, type Lang } from "@/lib/i18n";
import { useDict } from "@/components/i18n/DictProvider";
import {
  CONSENT_CHANGED_EVENT,
  CONSENT_OPEN_EVENT,
  readConsent,
  type Consent,
} from "@/lib/analytics/consent";
import { applyConsent } from "./applyConsent";

function subscribeConsent(cb: () => void) {
  window.addEventListener(CONSENT_CHANGED_EVENT, cb);
  return () => window.removeEventListener(CONSENT_CHANGED_EVENT, cb);
}

/**
 * Bottom consent bar. The server snapshot is "decided", so the banner is never
 * server-rendered (no hydration mismatch, no flash for visitors who already
 * chose); on the client it reads the cookie and appears only when no choice is
 * stored — or when reopened from the cookies page via {@link CONSENT_OPEN_EVENT}.
 * Accepting opts PostHog in (cookies + capture) and starts GA4; declining keeps
 * both off. One banner for every analytics provider the portal configures.
 */
export function ConsentBanner({ lang }: { lang: Lang }) {
  const decided = useSyncExternalStore(
    subscribeConsent,
    () => readConsent() !== null,
    () => true,
  );
  const [reopened, setReopened] = useState(false);

  useEffect(() => {
    function onOpen() {
      setReopened(true);
    }
    window.addEventListener(CONSENT_OPEN_EVENT, onOpen);
    return () => window.removeEventListener(CONSENT_OPEN_EVENT, onOpen);
  }, []);

  const t = useDict().consent;

  if (decided && !reopened) return null;

  function decide(value: Consent) {
    applyConsent(value);
    setReopened(false);
  }

  return (
    <div
      role="region"
      aria-label={t.message}
      className="fixed inset-x-0 bottom-0 z-50 border-t border-border bg-card text-card-foreground"
      style={{ boxShadow: "var(--shadow-lift)" }}
    >
      <Container className="flex flex-col gap-3 py-4 md:flex-row md:items-center md:justify-between">
        <p className="text-sm text-muted-foreground">
          {t.message}{" "}
          <Link href={langPath(lang, "/cookies")} className="underline hover:text-foreground">
            {t.learnMore}
          </Link>
        </p>
        <div className="flex shrink-0 items-center gap-3">
          <Button variant="secondary" size="compact" onClick={() => decide("denied")}>
            {t.decline}
          </Button>
          <Button variant="primary" size="compact" onClick={() => decide("granted")}>
            {t.accept}
          </Button>
        </div>
      </Container>
    </div>
  );
}
